import { useRef, useState } from 'react';
import { ChevronDown, Clock, Info } from 'lucide-react';
import { cn } from '@/utils/cn';
import { baseBudgetMin, IST_HOUR_OPTIONS } from '@/utils/hourlyBudgetSchedule';
import {
  HOUR_MULTIPLIER_OPTIONS,
  activeHourCountForSlots,
  canToggleHourOn,
  formatActiveHourCount,
  formatHourMultiplier,
  formatHourlyIncrementNote,
  totalBudgetForSlots,
  type HourSlotMap,
} from '@/utils/hourlyBudgetHourSlots';
import { formatSmartBudgetInr } from '@/utils/smartCampaignBudget';
import { scheduleMultiplierCellStyle } from '@/utils/heatmapColors';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/Popover';
import {
  ALL_WEEKDAYS,
  WEEKDAYS,
  type DayAmounts,
  type WeekdayIndex,
} from '@/utils/hourlyBudgetWeek';

type HourlyBudgetWeekGridProps = {
  slotsByDay: Record<WeekdayIndex, HourSlotMap>;
  amountsByDay: Record<WeekdayIndex, DayAmounts>;
  timezoneLabel: string;
  disabled?: boolean;
  selectionOnly?: boolean;
  validationMessage?: string | null;
  onToggleHour: (day: WeekdayIndex, hour: number) => void;
  onMultiplierChange: (day: WeekdayIndex, hour: number, multiplier: string) => void;
  onAmountChange: (day: WeekdayIndex, field: 'hourly' | 'base', value: string) => void;
  onCopyDay: (sourceDay: WeekdayIndex, targetDays: WeekdayIndex[]) => void;
  onClearDay: (day: WeekdayIndex) => void;
};

type FocusedCell = { day: WeekdayIndex; hour: number };

type HourPointerSnapshot = {
  day: WeekdayIndex;
  hour: number;
  wasSelected: boolean;
  at: number;
};

const DOUBLE_CLICK_MS = 500;

function parseMultiplier(raw: string): number | null {
  const parsed = parseFloat(raw);
  if (Number.isNaN(parsed) || parsed <= 0) return null;
  return parsed;
}

function heatmapHourLabel(label: string): string {
  return label.replace(':00', '');
}

function CopyToDaysMenu({
  sourceDay,
  disabled,
  onCopy,
}: {
  sourceDay: WeekdayIndex;
  disabled?: boolean;
  onCopy: (sourceDay: WeekdayIndex, targetDays: WeekdayIndex[]) => void;
}) {
  const [open, setOpen] = useState(false);
  const [targets, setTargets] = useState<WeekdayIndex[]>([]);
  const others = ALL_WEEKDAYS.filter((day) => day !== sourceDay);
  const source = WEEKDAYS[sourceDay];

  return (
    <Popover
      open={open}
      onOpenChange={(next) => {
        setOpen(next);
        if (next) setTargets(others);
      }}
    >
      <PopoverTrigger asChild>
        <button
          type="button"
          disabled={disabled}
          aria-haspopup="menu"
          aria-expanded={open}
          className="inline-flex items-center justify-center gap-0.5 rounded border border-border bg-surface px-1 py-0.5 text-[10px] font-medium text-foreground hover:bg-surface-muted disabled:opacity-50"
        >
          Copy to…
          <ChevronDown className="h-3 w-3" aria-hidden />
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        side="bottom"
        sideOffset={4}
        collisionPadding={8}
        className="z-[90] w-44 p-2.5"
      >
        <p className="mb-2 text-xs font-medium text-foreground">Copy {source.short} to</p>
        <div className="mb-2 flex flex-col gap-1">
          {WEEKDAYS.filter((day) => day.index !== sourceDay).map((day) => (
            <label key={day.index} className="flex items-center gap-2 text-xs text-foreground">
              <input
                type="checkbox"
                checked={targets.includes(day.index)}
                onChange={(event) =>
                  setTargets((prev) =>
                    event.target.checked
                      ? [...prev, day.index]
                      : prev.filter((d) => d !== day.index)
                  )
                }
              />
              {day.long}
            </label>
          ))}
        </div>
        <button
          type="button"
          disabled={targets.length === 0 || disabled}
          onClick={() => {
            onCopy(sourceDay, targets);
            setOpen(false);
          }}
          className="w-full rounded bg-brand px-2 py-1 text-xs font-medium text-white disabled:opacity-50"
        >
          Apply
        </button>
      </PopoverContent>
    </Popover>
  );
}

export function HourlyBudgetWeekGrid({
  slotsByDay,
  amountsByDay,
  timezoneLabel,
  disabled,
  selectionOnly = false,
  validationMessage,
  onToggleHour,
  onMultiplierChange,
  onAmountChange,
  onCopyDay,
  onClearDay,
}: HourlyBudgetWeekGridProps) {
  const [focused, setFocused] = useState<FocusedCell | null>(null);
  const [customDraft, setCustomDraft] = useState('');
  const hourPointerSnapshot = useRef<HourPointerSnapshot | null>(null);

  const otherDays = (day: WeekdayIndex) => ALL_WEEKDAYS.filter((d) => d !== day);

  const copyToAll = (day: WeekdayIndex) => {
    onCopyDay(day, otherDays(day));
  };

  const focusCell = (day: WeekdayIndex, hour: number) => {
    setFocused({ day, hour });
    setCustomDraft('');
  };

  const rememberHourPointer = (day: WeekdayIndex, hour: number, wasSelected: boolean) => {
    const current = hourPointerSnapshot.current;
    const now = Date.now();
    // The second press of a double-click must keep the first snapshot, so an
    // empty hour that the first click just turned on is not treated as selected.
    if (
      current != null &&
      current.day === day &&
      current.hour === hour &&
      now - current.at < DOUBLE_CLICK_MS
    ) {
      return;
    }
    hourPointerSnapshot.current = { day, hour, wasSelected, at: now };
  };

  const deselectHourOnDoubleClick = (day: WeekdayIndex, hour: number) => {
    const start = hourPointerSnapshot.current;
    hourPointerSnapshot.current = null;
    if (start == null || start.day !== day || start.hour !== hour || !start.wasSelected) return;
    onToggleHour(day, hour);
    setFocused((current) => (current?.day === day && current.hour === hour ? null : current));
  };

  const focusedMultiplier =
    focused == null ? null : (slotsByDay[focused.day]?.[focused.hour] ?? null);
  const focusedLabel =
    focused == null
      ? null
      : `${WEEKDAYS[focused.day].short} ${IST_HOUR_OPTIONS[focused.hour]?.label ?? ''}`;
  const focusedHourly =
    focused != null
      ? (amountsByDay[focused.day]?.hourly ?? '')
      : ALL_WEEKDAYS.map((day) => amountsByDay[day]?.hourly ?? '').find((value) => value.trim()) ??
        '';
  const incrementNote = formatHourlyIncrementNote(focusedHourly);

  const applyFocusedMultiplier = (next: string) => {
    if (focused == null) return;
    onMultiplierChange(focused.day, focused.hour, next);
  };

  const commitCustom = () => {
    if (focused == null) return;
    const next = parseMultiplier(customDraft);
    if (next == null) return;
    onMultiplierChange(focused.day, focused.hour, String(next));
    setCustomDraft('');
  };

  return (
    <div>
      <p className="mb-2 inline-flex items-center gap-1.5 text-xs text-foreground-muted">
        <Clock className="h-3.5 w-3.5 shrink-0" />
        All times are in {timezoneLabel}. Hours run down; each column is one weekday.
      </p>
      <div className="mb-2 flex flex-wrap items-center gap-1.5 rounded-lg border border-border bg-page px-2 py-1.5">
        {selectionOnly ? (
          <p className="text-xs text-foreground-muted">Click an hour to turn it on or off.</p>
        ) : focused && focusedMultiplier != null ? (
          <>
            <span className="mr-1 text-xs font-medium text-foreground">{focusedLabel}</span>
            {HOUR_MULTIPLIER_OPTIONS.map((option) => (
              <button
                key={option}
                type="button"
                disabled={disabled}
                onClick={() => applyFocusedMultiplier(option)}
                className={cn(
                  'rounded px-1.5 py-0.5 text-[11px] font-semibold tabular-nums',
                  focusedMultiplier === option
                    ? 'ring-2 ring-brand ring-offset-1'
                    : 'hover:opacity-90'
                )}
                style={scheduleMultiplierCellStyle(parseMultiplier(option))}
              >
                {formatHourMultiplier(option)}
              </button>
            ))}
            <label className="ml-1 inline-flex items-center gap-1 text-[11px] text-foreground-muted">
              Custom
              <input
                type="number"
                min="0.01"
                step="0.01"
                value={customDraft}
                placeholder="1.1"
                disabled={disabled}
                onChange={(event) => setCustomDraft(event.target.value)}
                onBlur={commitCustom}
                onKeyDown={(event) => {
                  if (event.key === 'Enter') {
                    event.preventDefault();
                    commitCustom();
                  }
                }}
                className="w-14 rounded border border-border bg-surface px-1 py-0.5 text-xs text-foreground"
              />
            </label>
            <button
              type="button"
              disabled={disabled}
              onClick={() => {
                if (focused == null) return;
                onToggleHour(focused.day, focused.hour);
                setFocused(null);
              }}
              className="ml-1 rounded border border-border bg-surface px-1.5 py-0.5 text-[11px] font-medium text-foreground hover:bg-surface-muted"
            >
              Off
            </button>
          </>
        ) : (
          <p className="text-xs text-foreground-muted">
            Click an empty hour to turn it on. Click a colored hour to change its multiplier.
            Double-click a colored hour to turn it off.
          </p>
        )}
      </div>
      <div className="overflow-x-auto rounded-xl border border-border">
        <table className="min-w-full border-collapse text-xs">
          <thead className="bg-page text-foreground-muted">
            <tr>
              <th className="sticky left-0 z-20 min-w-[74px] border-b border-r border-border bg-page px-2 py-2 text-left font-medium">
                Hour
              </th>
              {WEEKDAYS.map((day) => (
                <th
                  key={day.index}
                  className="border-b border-border px-2 py-2 text-center font-medium"
                >
                  {day.short}
                </th>
              ))}
            </tr>
            {selectionOnly ? null : (
            <tr>
              <th className="sticky left-0 z-20 border-b border-r border-border bg-page px-2 py-1.5 text-left font-medium">
                Base
              </th>
              {WEEKDAYS.map((day) => {
                const amounts = amountsByDay[day.index] ?? { hourly: '', base: '' };
                return (
                  <td key={day.index} className="border-b border-border px-1 py-1">
                    <input
                      type="number"
                      step="0.01"
                      min={baseBudgetMin()}
                      aria-label={`${day.long} base budget`}
                      value={amounts.base}
                      placeholder="Base"
                      disabled={disabled}
                      onChange={(event) => onAmountChange(day.index, 'base', event.target.value)}
                      className="w-full min-w-[4.5rem] rounded border border-border bg-surface px-1.5 py-1 text-center text-xs"
                    />
                  </td>
                );
              })}
            </tr>
            )}
            {selectionOnly ? null : (
            <tr>
              <th className="sticky left-0 z-20 border-b border-r border-border bg-page px-2 py-1.5 text-left font-medium">
                Hourly
              </th>
              {WEEKDAYS.map((day) => {
                const amounts = amountsByDay[day.index] ?? { hourly: '', base: '' };
                return (
                  <td key={day.index} className="border-b border-border px-1 py-1">
                    <input
                      type="number"
                      step="0.01"
                      min="0"
                      aria-label={`${day.long} hourly budget`}
                      value={amounts.hourly}
                      placeholder="Hourly"
                      disabled={disabled}
                      onChange={(event) => onAmountChange(day.index, 'hourly', event.target.value)}
                      className="w-full min-w-[4.5rem] rounded border border-border bg-surface px-1.5 py-1 text-center text-xs"
                    />
                  </td>
                );
              })}
            </tr>
            )}
            <tr>
              <th className="sticky left-0 z-20 border-b border-r border-border bg-page px-2 py-1.5 text-left font-medium">
                {' '}
              </th>
              {WEEKDAYS.map((day) => {
                const slots = slotsByDay[day.index] ?? {};
                return (
                  <td key={day.index} className="border-b border-border px-1 py-1 align-top">
                    <div className="flex flex-col items-stretch gap-1">
                      <button
                        type="button"
                        disabled={disabled}
                        onClick={() => copyToAll(day.index)}
                        className="rounded border border-border bg-surface px-1 py-0.5 text-[10px] font-medium text-foreground hover:bg-surface-muted disabled:opacity-50"
                      >
                        Copy to all
                      </button>
                      <CopyToDaysMenu
                        sourceDay={day.index}
                        disabled={disabled}
                        onCopy={onCopyDay}
                      />
                      <button
                        type="button"
                        disabled={disabled || Object.keys(slots).length === 0}
                        onClick={() => onClearDay(day.index)}
                        className="rounded border border-border bg-surface px-1 py-0.5 text-[10px] font-medium text-foreground-muted hover:bg-surface-muted disabled:opacity-50"
                      >
                        Clear
                      </button>
                    </div>
                  </td>
                );
              })}
            </tr>
          </thead>
          <tbody>
            {IST_HOUR_OPTIONS.map((opt) => (
              <tr key={opt.hour} className="border-b border-border">
                <td className="sticky left-0 z-10 whitespace-nowrap border-r border-border bg-surface px-2 py-1.5 text-center text-foreground-muted">
                  {heatmapHourLabel(opt.label)}
                </td>
                {WEEKDAYS.map((day) => {
                  const slots = slotsByDay[day.index] ?? {};
                  const selected = slots[opt.hour] != null;
                  const multiplier = slots[opt.hour] ?? '1';
                  const canSelect = selected || canToggleHourOn(slots, opt.hour);
                  const isFocused = focused?.day === day.index && focused.hour === opt.hour;
                  const style = scheduleMultiplierCellStyle(
                    selected ? parseMultiplier(multiplier) : null
                  );
                  return (
                    <td
                      key={day.index}
                      className="border-border p-0 text-center font-medium"
                      style={style}
                    >
                      <button
                        type="button"
                        aria-label={
                          selectionOnly
                            ? `${day.short} ${opt.label}${selected ? ' On' : ''}`
                            : `${day.short} ${opt.label}${
                                selected ? ` ${formatHourMultiplier(multiplier)}` : ''
                              }`
                        }
                        aria-pressed={selected}
                        disabled={disabled || !canSelect}
                        title={
                          selectionOnly
                            ? `${day.short} ${opt.label}: ${selected ? 'on' : 'off'}`
                            : selected
                              ? `${day.short} ${opt.label}: ${formatHourMultiplier(multiplier)}`
                              : `${day.short} ${opt.label}: off`
                        }
                        onPointerDown={() => {
                          if (selectionOnly) return;
                          rememberHourPointer(day.index, opt.hour, selected);
                        }}
                        onClick={() => {
                          if (selectionOnly) {
                            onToggleHour(day.index, opt.hour);
                            return;
                          }
                          if (selected) {
                            focusCell(day.index, opt.hour);
                            return;
                          }
                          onToggleHour(day.index, opt.hour);
                          focusCell(day.index, opt.hour);
                        }}
                        onDoubleClick={() => {
                          if (selectionOnly) return;
                          deselectHourOnDoubleClick(day.index, opt.hour);
                        }}
                        className={cn(
                          'flex h-8 w-full min-w-[3.5rem] items-center justify-center px-2 text-[11px] font-semibold tabular-nums disabled:cursor-not-allowed',
                          !selectionOnly && isFocused && 'ring-2 ring-inset ring-brand'
                        )}
                        style={{ color: style.color }}
                      >
                        {selected ? (selectionOnly ? 'On' : formatHourMultiplier(multiplier)) : ''}
                      </button>
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr className="border-t border-border">
              <td
                className="sticky left-0 z-10 whitespace-nowrap border-r border-border bg-page px-2 py-1.5 text-center text-xs font-semibold text-foreground"
              >
                Total selected hours
              </td>
              {WEEKDAYS.map((day) => {
                const slots = slotsByDay[day.index] ?? {};
                const activeHours = activeHourCountForSlots(slots);
                return (
                  <td
                    key={day.index}
                    aria-label={`${day.long} total selected hours: ${formatActiveHourCount(activeHours)}`}
                    className="bg-page px-2 py-1.5 text-center text-xs font-semibold tabular-nums text-foreground"
                  >
                    {formatActiveHourCount(activeHours)}
                  </td>
                );
              })}
            </tr>
            {selectionOnly ? null : (
            <tr className="border-t border-border">
              <td
                className="sticky left-0 z-10 whitespace-nowrap border-r border-border bg-surface-muted px-2 py-1.5 text-center text-xs font-semibold text-foreground"
              >
                Total budget
              </td>
              {WEEKDAYS.map((day) => {
                const slots = slotsByDay[day.index] ?? {};
                const amounts = amountsByDay[day.index] ?? { hourly: '', base: '' };
                const totalBudget = totalBudgetForSlots(slots, amounts.hourly, amounts.base);
                const formattedBudget = formatSmartBudgetInr(totalBudget);
                return (
                  <td
                    key={day.index}
                    aria-label={`${day.long} total budget: ${formattedBudget}`}
                    className="bg-surface-muted px-2 py-1.5 text-center text-xs font-semibold tabular-nums text-foreground"
                  >
                    {formattedBudget}
                  </td>
                );
              })}
            </tr>
            )}
          </tfoot>
        </table>
      </div>
      {validationMessage && <p className="mt-2 text-xs text-error">{validationMessage}</p>}
      {selectionOnly ? null : (
      <div className="mt-2 flex flex-wrap items-center gap-3 text-[11px] text-foreground-muted">
        <span>Multiplier</span>
        {[1, 2, 3, 5].map((value) => (
          <span key={value} className="inline-flex items-center gap-1">
            <span
              className="inline-block h-3 w-5 rounded-sm border border-border"
              style={scheduleMultiplierCellStyle(value)}
            />
            {formatHourMultiplier(String(value))}
          </span>
        ))}
      </div>
      )}
      <div className="mt-2 flex items-start gap-2 rounded-lg bg-brand-subtle px-3 py-2 text-xs text-brand-deep">
        <Info className="mt-0.5 h-3.5 w-3.5 shrink-0" />
        {selectionOnly ? (
          <p>
            Painted hours use one change for the whole schedule. The change starts when a painted
            block begins and the previous values are written back when the block ends. Copy and
            clear still apply. A day can have at most 12 separate hour ranges.
          </p>
        ) : (
        <p>
          1× adds that day’s hourly budget; 2× adds twice that hourly budget. {incrementNote} Darker
          cells are higher multipliers. Click an empty hour to turn it on at 1×, then use the bar
          above the grid to change the multiplier or turn it off. Double-click a colored hour to
          turn it off. Copy to all applies that column’s
          amounts and hours to every other day. A day with both amounts empty is skipped; base budget
          only still midnight-resets and does not increment.
        </p>
        )}
      </div>
    </div>
  );
}
